import IGlobalConfig from "../types/IGlobalConfig";
import {
  CreateAiChatSessionRequest,
  CreateAiChatSessionResponse,
  GetAiChatSessionResponse,
  ListAiChatSessionsResponse,
  SendAiChatMessageRequest,
  SendAiChatMessageResponse,
} from "../types/services/IAiChatSession";
import { customFetchV2 } from "../utils/fetch";
import { resolveFirebaseIdTokenForApiCall } from "../utils/firebaseToken";

function resolveAdminServiceUrl(config: IGlobalConfig): string {
  return (config && config.adminServiceUrl) || "";
}

function chatSessionsPath(charityId: string): string {
  return `/api/v1/${encodeURIComponent(charityId)}/ai/chat-sessions`;
}

export class AiChatSessionService {
  private static async authQueryToken(token: string): Promise<string> {
    return resolveFirebaseIdTokenForApiCall(token);
  }

  private static buildUrl(
    config: IGlobalConfig,
    path: string,
    token: string
  ): string {
    const base = resolveAdminServiceUrl(config).replace(/\/$/, "");
    return `${base}${path}?token=${encodeURIComponent(token)}`;
  }

  static async createSession(
    config: IGlobalConfig,
    serviceAccessToken: string,
    charityId: string,
    body: CreateAiChatSessionRequest
  ): Promise<CreateAiChatSessionResponse> {
    const resolved = await AiChatSessionService.authQueryToken(
      serviceAccessToken
    );
    const url = AiChatSessionService.buildUrl(
      config,
      chatSessionsPath(charityId),
      resolved
    );

    return customFetchV2({
      url,
      method: "POST",
      mode: "cors",
      body: JSON.stringify(body),
      headers: {
        "content-type": "application/json",
      },
    }) as Promise<CreateAiChatSessionResponse>;
  }

  static async listSessions(
    config: IGlobalConfig,
    serviceAccessToken: string,
    charityId: string
  ): Promise<ListAiChatSessionsResponse> {
    const resolved = await AiChatSessionService.authQueryToken(
      serviceAccessToken
    );
    const url = AiChatSessionService.buildUrl(
      config,
      chatSessionsPath(charityId),
      resolved
    );

    return customFetchV2({
      url,
      method: "GET",
      mode: "cors",
      headers: {
        "content-type": "application/json",
      },
    }) as Promise<ListAiChatSessionsResponse>;
  }

  static async getSession(
    config: IGlobalConfig,
    serviceAccessToken: string,
    charityId: string,
    sessionId: string
  ): Promise<GetAiChatSessionResponse> {
    const resolved = await AiChatSessionService.authQueryToken(
      serviceAccessToken
    );
    const url = AiChatSessionService.buildUrl(
      config,
      `${chatSessionsPath(charityId)}/${encodeURIComponent(sessionId)}`,
      resolved
    );

    return customFetchV2({
      url,
      method: "GET",
      mode: "cors",
      headers: {
        "content-type": "application/json",
      },
    }) as Promise<GetAiChatSessionResponse>;
  }

  static async sendMessage(
    config: IGlobalConfig,
    serviceAccessToken: string,
    charityId: string,
    sessionId: string,
    body: SendAiChatMessageRequest
  ): Promise<SendAiChatMessageResponse> {
    const resolved = await AiChatSessionService.authQueryToken(
      serviceAccessToken
    );
    const url = AiChatSessionService.buildUrl(
      config,
      `${chatSessionsPath(charityId)}/${encodeURIComponent(sessionId)}/messages`,
      resolved
    );

    return customFetchV2({
      url,
      method: "POST",
      mode: "cors",
      body: JSON.stringify(body),
      headers: {
        "content-type": "application/json",
      },
    }) as Promise<SendAiChatMessageResponse>;
  }
}
